import { Policy, PolicyBillingNotification } from "@deathstar/types/northstar";
import { DateTime } from "@deathstar/types/util";
import React from "react";

const dateSorter = DateTime.Sorter("DESC");

function findFutureNotification(notifications: PolicyBillingNotification[]): PolicyBillingNotification | undefined {
    return notifications.find((notification) => {
        const dateScheduled = new DateTime(notification.dateScheduled);
        if (
            !notification.dateCompleted &&
            !notification.skipped &&
            notification.sendOnScheduledDate &&
            dateScheduled.isAfter(DateTime.today, { excludeTime: true })
        ) {
            return notification;
        }
        return null;
    });
}

interface ReducerState {
    billingNotifications: PolicyBillingNotification[];
    hasFutureNotification: boolean;
    daysUntil: number;
}

export enum ActionType {
    ENABLE_TOGGLED,
    NOTIFICATIONS_UPDATED,
}

interface EnableToggledAction {
    type: ActionType.ENABLE_TOGGLED;
    payload: PolicyBillingNotification;
}

interface NotificationsUpdatedAction {
    type: ActionType.NOTIFICATIONS_UPDATED;
    payload: PolicyBillingNotification[];
}

type Action = EnableToggledAction | NotificationsUpdatedAction;

function reducer(state: ReducerState, action: Action): ReducerState {
    if (action.type === ActionType.ENABLE_TOGGLED) {
        const billingNotifications = state.billingNotifications.map((n) => {
            if (n.id === action.payload?.id) {
                return action.payload;
            }
            return n;
        });
        const notification = findFutureNotification(billingNotifications);
        return {
            billingNotifications,
            hasFutureNotification: !!notification,
            daysUntil: notification ? DateTime.differenceInCalendarDays(notification.dateScheduled, DateTime.today) : 0,
        };
    } else if (action.type === ActionType.NOTIFICATIONS_UPDATED) {
        return createInitialState({
            billingNotifications: action.payload || [],
            hasFutureNotification: false,
            daysUntil: 0,
        });
    } else {
        console.warn(`AutomatedBillingIcon.tsx ~ reducer ~ Unknown action type:`, action);
    }
    return state;
}

function createInitialState(state: ReducerState): ReducerState {
    const sorted =
        state.billingNotifications
            ?.map((n) => ({
                ...n,
                dateScheduled: new DateTime(n.dateScheduled),
            }))
            .sort((a, b) => dateSorter(a.dateScheduled, b.dateScheduled)) || [];
    const notification = findFutureNotification(sorted);
    return {
        billingNotifications: sorted,
        hasFutureNotification: !!notification,
        daysUntil: notification ? DateTime.differenceInCalendarDays(notification.dateScheduled, DateTime.today) : 0,
    };
}


export function usePolicyNotificationsTable({ policy }: { policy: Policy;}): [state: ReducerState, dispatch: React.Dispatch<Action>] {
    const [state, dispatch] = React.useReducer(
            reducer,
            {
                billingNotifications: policy.billingNotifications || [],
                hasFutureNotification: false,
                daysUntil: 0,
            },
            createInitialState
        );
    
        React.useEffect(() => {
            dispatch({
                type: ActionType.NOTIFICATIONS_UPDATED,
                payload: policy.billingNotifications || [],
            });
        }, [policy?.billingNotifications]);

    return [state,
        dispatch]
}